// The app's shared state: settings, progress, the last round, the national total.
//
// Plain module refs, the same shape the chassis ships. Settings are hydrated
// from `prefs` and written back on every change; progress is its own document
// (see lib/progress.js); the last result and run live only in memory, because
// Results is always reached from a round that just finished.

import { ref, reactive, watch } from 'vue';
import { prefs } from './core/storage.js';
import { prefersReducedMotion } from './core/a11y.js';
import { setMuted } from './core/audio.js';
import { setHaptics } from './core/util.js';
import { loadProgress, saveProgress } from './lib/progress.js';
import { refreshPalette } from './lib/palette.js';
import { cachedTotal } from './lib/national.js';

const SETTINGS_KEYS = ['reducedMotion', 'highContrast', 'practiceSpeed', 'sound', 'music', 'haptics'];

function defaultSettings() {
  return {
    reducedMotion: prefersReducedMotion(),
    highContrast: false,
    practiceSpeed: false,
    sound: true,
    music: true,
    haptics: true,
  };
}

function hydrateSettings() {
  const d = defaultSettings();
  const out = {};
  for (const k of SETTINGS_KEYS) {
    const v = prefs[`settings.${k}`];
    out[k] = typeof v === 'boolean' ? v : d[k];
  }
  return out;
}

/** Accessibility and audio preferences. */
export const settings = reactive(hydrateSettings());

/** Progress: bests, unlocks, seen mechanics. */
export const progress = reactive(loadProgress());

/** The round that just finished, or null. */
export const lastResult = ref(null);

/** How that round was set up: { mode, seed, day?, code?, level, practice, target? }. */
export const lastRun = ref(null);

/** The national counter as last seen: { total, fetchedAt } or null. */
export const national = ref(cachedTotal());

/** Reduced motion is on if either the OS or the toggle says so. */
export function reducedMotion() {
  return settings.reducedMotion || prefersReducedMotion();
}

function applySettings() {
  setMuted(!settings.sound);
  setHaptics(!!settings.haptics);
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.dataset.reducedMotion = settings.reducedMotion ? '1' : '0';
    root.dataset.highContrast = settings.highContrast ? '1' : '0';
  }
  refreshPalette();
}

export function saveSettings() {
  for (const k of SETTINGS_KEYS) prefs[`settings.${k}`] = !!settings[k];
  applySettings();
}

watch(settings, saveSettings, { deep: true });
applySettings();

export function setLastResult(result, run) {
  lastResult.value = result;
  lastRun.value = run;
}

export function persistProgress() {
  saveProgress(JSON.parse(JSON.stringify(progress)));
}

export function setNational(entry) {
  if (entry && Number.isFinite(entry.total)) national.value = { total: entry.total, fetchedAt: entry.fetchedAt ?? Date.now() };
}

/** Thousands separators, or a dash when there is nothing honest to show. */
export function formatTotal(n) {
  return Number.isFinite(n) ? new Intl.NumberFormat('en-AU').format(n) : '—';
}
